import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import { ApiError } from "../../services/api/BaseApi";
import type { GameDetail } from "../../types/game";
import { Skeleton, SkeletonText } from "../../components/Skeleton";
import MediaCarousel from "../../components/MediaCarousel/MediaCarousel";
import NewsSection from "../../components/NewsSection/NewsSection";
import { buildCarouselMedia } from "./mediaTransform";
import noImage from "../../assets/no-image.jpg";
import styles from "./GamePage.module.scss";

type PageState = "loading" | "success" | "not-found" | "error";

const FETCH_TIMEOUT_MS = 10_000;

const GamePage: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>("loading");
  const [game, setGame] = useState<GameDetail | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const fetchGame = useCallback(async (id: string) => {
    setState("loading");
    setGame(null);
    setErrorMessage("");

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      const result = await Promise.race([
        api.games.getGameById(id),
        new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => {
            reject(new Error("Request timed out"));
          }, FETCH_TIMEOUT_MS);
        }),
      ]);

      clearTimeout(timeoutId);
      setGame(result.game);
      setState("success");
    } catch (err) {
      clearTimeout(timeoutId);

      if (err instanceof ApiError && err.status === 404) {
        setState("not-found");
      } else {
        setState("error");
        setErrorMessage(
          err instanceof Error ? err.message : "An unexpected error occurred"
        );
      }
    }
  }, []);

  useEffect(() => {
    if (!gameId) {
      setState("error");
      setErrorMessage("Invalid game ID");
      return;
    }

    fetchGame(gameId);
  }, [gameId, fetchGame]);

  // Loading state
  if (state === "loading") {
    return (
      <div className={styles.gamePage} data-testid="game-page-loading">
        <div className={styles.gameContent}>
          {/* Left column: detail card */}
          <div className={styles.leftColumn}>
            <Skeleton height="400px" width="100%" borderRadius="var(--radius-sm)" />
            <SkeletonText lines={5} lineHeight="0.85rem" gap="1rem" lastLineWidth="40%" />
          </div>
          {/* Right column: media + description */}
          <div className={styles.rightColumn}>
            <Skeleton height="2rem" width="50%" />
            <Skeleton height="300px" width="100%" borderRadius="var(--radius-md)" />
            <SkeletonText lines={4} lineHeight="0.85rem" gap="0.6rem" lastLineWidth="70%" />
          </div>
        </div>
      </div>
    );
  }

  // Not-found state
  if (state === "not-found") {
    return (
      <div className={styles.gamePage} data-testid="game-page-not-found">
        <h2>Game not found</h2>
        <p>The game you're looking for doesn't exist or has been removed.</p>
        <Link to="/" className={styles.backLink}>
          ← Back to Home
        </Link>
      </div>
    );
  }

  // Error state
  if (state === "error") {
    return (
      <div className={styles.gamePage} data-testid="game-page-error">
        <h2>Something went wrong</h2>
        <p>{errorMessage || "Failed to load game data."}</p>
        {gameId && (
          <button
            className={styles.retryButton}
            onClick={() => fetchGame(gameId)}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  // Success state
  const descriptionText = game?.summary || game?.description;
  const carouselMedia = game ? buildCarouselMedia(game.heroUrl, game.screenshots, game.trailers) : [];

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className={styles.gamePage} data-testid="game-page-success">
      <button
        className={styles.backButton}
        onClick={() => navigate(-1)}
        aria-label="Go back"
      >
        ← Back
      </button>
      {game && (
        <div className={styles.gameContent}>
          {/* ─── Left Column: Game Details Sidebar ─── */}
          <div className={styles.leftColumn}>
            {/* Cover + Quick Info Card */}
            <div className={styles.detailCard}>
              {game.coverUrl && (
                <img
                  className={styles.coverImage}
                  src={game.coverUrl}
                  alt={`${game.name} cover`}
                  onError={(e) => {
                    if (e.currentTarget.src !== noImage) {
                      e.currentTarget.src = noImage;
                    }
                  }}
                />
              )}

              {/* Release Date */}
              {game.releaseDate && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Release Date</span>
                  <span className={styles.releaseDate}>
                    {formatDate(game.releaseDate)}
                  </span>
                </div>
              )}

              {/* Developers */}
              {game.developers.length > 0 && (
                <div className={styles.detailRow} data-testid="game-developers-section">
                  <span className={styles.detailLabel}>Developer</span>
                  <span className={styles.detailValue}>
                    {game.developers.join(", ")}
                  </span>
                </div>
              )}

              {/* Publishers */}
              {game.publishers.length > 0 && (
                <div className={styles.detailRow} data-testid="game-publishers-section">
                  <span className={styles.detailLabel}>Publisher</span>
                  <span className={styles.detailValue}>
                    {game.publishers.join(", ")}
                  </span>
                </div>
              )}

              {/* Genres */}
              {game.genres.length > 0 && (
                <div className={styles.detailRow} data-testid="game-genres-section">
                  <span className={styles.detailLabel}>Genres</span>
                  <ul className={styles.tagList}>
                    {game.genres.map((genre) => (
                      <li key={genre} className={styles.tag}>{genre}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Platforms */}
              {game.platforms.length > 0 && (
                <div className={styles.detailRow} data-testid="game-platforms-section">
                  <span className={styles.detailLabel}>Platforms</span>
                  <ul className={styles.tagList}>
                    {game.platforms.map((platform) => (
                      <li key={platform} className={styles.tag}>{platform}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Links */}
              {game.links.length > 0 && (
                <div className={styles.detailRow} data-testid="game-links-section">
                  <span className={styles.detailLabel}>Links</span>
                  <ul className={styles.linkList}>
                    {game.links.map((link, index) => {
                      let domain = "";
                      try {
                        domain = new URL(link.url).hostname.replace(/^www\./, "");
                      } catch { /* ignore */ }
                      return (
                        <li key={`${link.type}-${index}`}>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.linkChip}
                          >
                            {link.type.toLowerCase() !== "other" && (
                              <span className={styles.linkChipType}>{link.type}</span>
                            )}
                            {domain && <span className={link.type.toLowerCase() !== "other" ? styles.linkChipDomain : styles.linkChipType}>{domain}</span>}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* ─── Right Column: Media + Description ─── */}
          <div className={styles.rightColumn}>
            {/* Game Title */}
            <h1 className={styles.gameTitle}>{game.name}</h1>

            {/* Media Carousel (trailers + screenshots) */}
            {carouselMedia.length > 0 && (
              <section className={styles.mediaSection} data-testid="game-media-section">
                <MediaCarousel
                  media={carouselMedia}
                  imageFallbackUrl={noImage}
                />
              </section>
            )}

            {/* Description */}
            {descriptionText && (
              <section className={styles.descriptionSection} data-testid="game-description-section">
                <h2 className={styles.sectionHeading}>About This Game</h2>
                <p className={styles.descriptionText}>{descriptionText}</p>
              </section>
            )}

            {/* News Section — fetches independently, does not block page render */}
            {gameId && <NewsSection gameId={gameId} />}
          </div>
        </div>
      )}
    </div>
  );
};

export default GamePage;
